<template>
  <div class="container mt-4" dir="rtl">
    
    <!-- 1. لوحة التحكم والفلاتر -->
    <div class="card no-print mb-4 shadow-sm border-top-primary">
      <div class="card-header bg-white">
        <h5 class="fw-bold text-primary mb-0"><i class="fas fa-print ms-2"></i> طباعة أظرف اللجان (إحصائيات)</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
            
            <!-- الترم -->
            <div class="col-md-2">
                <label class="fw-bold small">الفصل الدراسي</label>
                <select v-model="form.term_id" class="form-select">
                    <option value="1">الأول</option>
                    <option value="2">الثاني</option>
                </select>
            </div>

            <!-- الإدارة -->
            <div class="col-md-3">
                <label class="fw-bold small">الإدارة التعليمية</label>
                <select v-model="form.admin_id" class="form-select" @change="fetchCommittees">
                    <option v-for="adm in administrations" :key="adm.id" :value="adm.id">{{ adm.dep_name }}</option>
                </select>
            </div>

            <!-- نوع التقرير (نطاق البحث) -->
            <div class="col-md-4">
                <label class="fw-bold small">نطاق الطباعة</label>
                <div class="d-flex gap-3 mt-1">
                    <div class="form-check">
                        <input class="form-check-input" type="radio" name="scope" id="scopeAll" value="all" v-model="form.scope">
                        <label class="form-check-label fw-bold" for="scopeAll">طباعة الإدارة بالكامل</label>
                    </div>
                    <div class="form-check">
                        <input class="form-check-input" type="radio" name="scope" id="scopeOne" value="one" v-model="form.scope">
                        <label class="form-check-label fw-bold" for="scopeOne">لجنة محددة</label>
                    </div>
                </div>
            </div>

            <!-- اختيار اللجنة (يظهر فقط لو اخترنا لجنة محددة) -->
            <div class="col-md-3" v-if="form.scope === 'one'">
                <label class="fw-bold small">اختر اللجنة</label>
                <select v-model="form.committee_id" class="form-select">
                    <option v-for="c in committeesList" :key="c.id" :value="c.id">
                        {{ c.commitee_name }}
                    </option>
                </select>
            </div>

            <!-- الأزرار -->
            <div class="col-12 text-center mt-3 border-top pt-3">
                <button @click="fetchData" class="btn btn-primary px-5 fw-bold ms-2" :disabled="loading">
                    <span v-if="loading" class="spinner-border spinner-border-sm"></span>
                    <span v-else><i class="fas fa-search ms-2"></i> عرض البيانات</span>
                </button>
                <button @click="print" class="btn btn-dark px-5 fw-bold" :disabled="reports.length === 0">
                    <i class="fas fa-print ms-2"></i> طباعة
                </button>
            </div>
        </div>
      </div>
    </div>

    <!-- 2. منطقة الطباعة (Loop لطباعة عدة أظرف) -->
    <div id="printMe" v-if="reports.length > 0" class="print-area">
      
      <!-- بداية الحلقة: لكل تقرير (لجنة) ظرف مستقل -->
      <div v-for="(reportData, index) in reports" :key="index" class="envelope-wrapper" style="page-break-after: always;">
          
          <div class="envelope-box">
            
            <!-- أ. الترويسة -->
            <div class="envelope-header mb-2 text-center" dir="rtl">
                <h2 class="fw-bold text-decoration-underline mb-1">إحصاء وتفصيل بطاقات تحقيق الشخصية</h2>
                <h5 class="fw-bold">الشهادة الإعدادية - {{ termName }} 2026</h5>
                
                <div class="d-flex justify-content-between align-items-center mt-3 border border-2 border-dark p-2 rounded bg-light">
                    <div class="fs-4 fw-bold text-start">مقر اللجنة: {{ reportData.committee.commitee_name }}</div>
                    <div class="text-center">
                        <span class="badge bg-white text-dark border border-dark fs-6">
                             {{ reportData.committee.department ? reportData.committee.department.dep_name : '...' }}
                        </span>
                    </div>
                    <div class="fs-4 fw-bold">رقم اللجنة: ( {{ reportData.committee.id }} )</div>
                </div>
            </div>

            <!-- ب. الجدول التفصيلي -->
            <div class="table-container mt-3" dir="rtl">
                <table class="table table-bordered border-dark text-center align-middle stats-table mb-0">
                    <thead>
                        <tr class="bg-light">
                            <th rowspan="2" style="width: 22%">المدرسة</th>
                            <!-- النطاقات -->
                            <th colspan="2" class="bg-primary-subtle">نطاق الأساسي</th>
                            <th colspan="2" class="bg-warning-subtle text-danger">أرقام  ( التلاحيق )</th>
                            <!-- التفاصيل -->
                            <th colspan="3">تفاصيل القيد</th>
                            <!-- الديانة -->
                            <th colspan="2">الديانة</th>
                            <th rowspan="2" class="bg-dark text-white" style="width: 8%">الإجمالي</th>
                        </tr>
                        <tr class="bg-light sub-header">
                            <th class="bg-primary-subtle small-th">نظامي</th>
                            <th class="bg-primary-subtle small-th">منازل</th>
                            <th class="bg-warning-subtle small-th text-danger">نظامي</th>
                            <th class="bg-warning-subtle small-th text-danger">منازل</th>
                            
                            <th>نظامي</th>
                            <th>منازل</th>
                            <th class="bg-info-subtle">دمج</th>
                            
                            <th>مسلم</th>
                            <th>مسيحي</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="school in reportData.schools" :key="school.school_id">
                            <td class="text-start pe-1 fw-bold fs-6 fs-20">{{ school.school.school_name }}</td>
                            
                            <!-- النطاقات -->
                            <td class="range-cell fw-bold fs-4">{{ formatRange(school.reg_min, school.reg_max) }}</td>
                            <td class="range-cell fw-bold fs-4">{{ formatRange(school.home_min, school.home_max) }}</td>
                            <td class="range-cell bg-warning-subtle text-danger fw-bold fs-4 tk-cell">{{ school.tk_reg_seats || '-' }}</td>
                            <td class="range-cell bg-warning-subtle text-danger fw-bold fs-4 tk-cell">{{ school.tk_home_seats || '-' }}</td>
                            
                            <!-- الأعداد -->
                            <td class="fw-bold fs-4">{{ school.count_regular }}</td>
                            <td class="fw-bold fs-4">{{ school.count_homes }}</td>
                            <td class="bg-info-subtle fw-bold fs-4" >{{ school.count_damg }}</td>
                            
                            <td  class="fw-bold fs-4">{{ school.count_muslim }}</td>
                            <td  class="fw-bold fs-4">{{ school.count_christian }}</td>
                            
                            <td class="fw-bold fs-4 bg-light">{{ school.total_students }}</td>
                        </tr>
                    </tbody>
                    <!-- التذييل -->
                    <tfoot>
                        <tr class="total-row">
                            <td class="text-center pe-2 fw-bold">الإجمالي العام</td>
                            <td class="range-cell font-monospace small fw-bold fs-4">{{ formatRange(reportData.grand_total.reg_min, reportData.grand_total.reg_max) }}</td>
                            <td class="range-cell font-monospace small fw-bold fs-4">{{ formatRange(reportData.grand_total.home_min, reportData.grand_total.home_max) }}</td>
                            <td class="range-cell bg-warning-subtle text-danger fw-bold fs-4 tk-cell small">{{ reportData.grand_total.tk_reg_seats || '-' }}</td>
                            <td class="range-cell bg-warning-subtle text-danger fw-bold fs-4 tk-cell small">{{ reportData.grand_total.tk_home_seats || '-' }}</td>
                            
                            <td class="fw-bold fs-4">{{ reportData.grand_total.regular }}</td>
                            <td class="fw-bold fs-4">{{ reportData.grand_total.homes }}</td>
                            <td class="bg-info-subtle text-dark fw-bold fs-4">{{ reportData.grand_total.damg }}</td>
                            
                            <td class="fw-bold fs-4">{{ reportData.grand_total.muslim }}</td>
                            <td class="fw-bold fs-4">{{ reportData.grand_total.christian }}</td>
                            
                            <td class="bg-dark text-white fs-3">{{ reportData.grand_total.students }}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            <!-- ج. صندوق الإجمالي -->
            <div class="footer-count mt-4 text-center">
                <div class="total-cards-box">
                    <h4 class="mb-2 fw-bold">إجمالي البطاقات بالظرف</h4>
                    <div class="big-number fw-bold fs-2">{{ reportData.grand_total.students }} بطاقة</div>
                </div>
            </div>
            <div class="row">
                <div class="col-12 text-start">
                     <h4 class="mb-2 fw-bold">  محتويات مظروف رئيس اللجنة</h4>

                    <ol class="fw-bold">
                    <li>  تعليمات رئيس اللجنة</li>
                    <li>بطاقات تحقيق الشخصية للطلاب</li>
                    <li>كشوف المناداة</li>
                    <!-- <li> <span class="badge bg-white text-dark border border-dark fs-6">
                        
                        بيانات دخول رئيس اللجنة على المنظومة الرقمية 
                    </span>
                        </li> -->
                    </ol>  
                 
                </div>
            </div>
          </div>
          
          <!-- فاصل الصفحات (مهم جداً عند طباعة الإدارة بالكامل) -->
          <div class="page-break"></div>

      </div>
    </div>

    <!-- رسالة فارغة -->
    <div v-else-if="searched && !loading" class="alert alert-info text-center mt-4 no-print">
        لا توجد بيانات مطابقة لعملية البحث.
    </div>

  </div>
</template>

<script>
import api from '@/api/axios';

export default {
  data() {
    return {
      loading: false,
      searched: false,
      administrations: [], // قائمة الإدارات
      committeesList: [],  // قائمة اللجان (تتحدث حسب الإدارة)
      reports: [],         // مصفوفة التقارير (قد تحتوي تقرير واحد أو أكثر)
      
      form: {
        term_id: 1,
        admin_id: '',
        scope: 'one', // 'one' or 'all'
        committee_id: ''
      }
    };
  },
  computed: {
      termName() {
          return this.form.term_id == 1 ? 'الفصل الدراسى الأول' : 'الفصل الدراسى  الثاني';
      }
  },
  mounted() {
      // جلب الإدارات أولاً
      this.fetchAdmins();
  },
  methods: {
    async fetchAdmins() {
        try {
            const res = await api.get('getadmins'); 
            this.administrations = res.data;
         
        } catch(e) {}
    },
    
    async fetchCommittees() {
        if(!this.form.admin_id) return;
        this.committeesList = [];
        this.form.committee_id = ''; // تصفير اللجنة
        
        try {
            const res = await api.get('/admin/getCommittee', { params: { admin_id: this.form.admin_id } });
            this.committeesList = res.data;
        } catch(e) {}
    },

    async fetchData() {
        if(!this.form.admin_id) return alert("اختر الإدارة");
        if(this.form.scope === 'one' && !this.form.committee_id) return alert("اختر اللجنة");

        this.loading = true;
        this.searched = true;
        this.reports = [];

        try {
            const res = await api.get('/admin/committee-envelope', { params: this.form });
            // الباك إند يرجع مصفوفة دائماً
            this.reports = res.data;
        } catch(e) { 
            alert("حدث خطأ في جلب البيانات");
        } finally {
            this.loading = false;
        }
    },

    formatRange(min, max) {
        if (!min || !max) return '-';
        if (min == max) return min;
        return `${min} : ${max}`;
    },
    print() {
        this.$htmlToPaper("printMe");
    }
  }
};
</script>

<style scoped>
.border-top-primary { border-top: 4px solid #0d6efd; }

@media print {
    @page {
        size: A4 landscape;
        margin: 10mm;
    }
    body * { visibility: hidden; }
    .no-print { display: none !important; }
    #printMe, #printMe * { visibility: visible; }
    #printMe {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
    }

    /* كلاس لكل ظرف لضمان الفواصل */
    .envelope-wrapper {
        width: 100%;
        height: 100%; /* ارتفاع الصفحة */
        page-break-after: always; /* فاصل إجباري بعد كل ظرف */
        display: flex;
        justify-content: center;
        align-items: center; /* توسيط في الصفحة */
    }

    /* الإطار الخارجي للظرف */
    .envelope-box {
        width: 100%;
        max-width: 280mm;
        border: 4px double #000;
        padding: 20px;
        border-radius: 15px;
        background: #fff;
    }

    /* باقي التنسيقات (الجدول، النصوص) كما هي */
    .stats-table th {
        background-color: #d1d1d1 !important;
        font-weight: bold;
        font-size: 11pt;
        border: 2px solid #000 !important;
        -webkit-print-color-adjust: exact;
    }
    .stats-table td {
        border: 2px solid #000 !important;
        font-size: 13pt;
        font-weight: bold;
        padding: 5px;
    }
    .bg-primary-subtle { background-color: #cfe2ff !important; -webkit-print-color-adjust: exact; }
    .bg-warning-subtle { background-color: #fff3cd !important; -webkit-print-color-adjust: exact; }
    .bg-info-subtle { background-color: #cff4fc !important; -webkit-print-color-adjust: exact; }
    
    .tk-cell {
        font-size: 10pt !important;
        white-space: normal;
        word-break: break-all;
        max-width: 120px;
        line-height: 1.1;
    }
    
    .total-cards-box {
        border: 3px dashed #000;
        padding: 10px;
        border-radius: 10px;
        display: inline-block;
        min-width: 40%;
        background-color: #f8f9fa !important;
        -webkit-print-color-adjust: exact;
    }
    .big-number {
        font-size: 30pt;
        font-weight: 900;
        border-bottom: 4px solid #000;
        display: inline-block;
    }
}
</style>